import React, { useEffect, useState } from 'react';
import {
  api,
  GLOBAL_COLORS,
  IBLSpinLoader,
  useIblTranslation,
  getUserAdminCapabilities,
  getUserName,
} from '@acilearning/ibl-web-react-common';
import './App.css';

import '@acilearning/ibl-web-react-common/index.css';
import route from './routes';
import { AppDataContext, isJSON } from '@acilearning/ibl-web-react-common';
import { RouterProvider } from 'react-router-dom';
import './i18n';
import { createGlobalStyle } from 'styled-components';

import '@shoelace-style/shoelace/dist/themes/light.css';
import { setBasePath } from '@shoelace-style/shoelace/dist/utilities/base-path';
import useNotifications from './hooks/useNotifications';
import { TourProvider } from '@reactour/tour';

setBasePath(
  'https://cdn.jsdelivr.net/npm/@shoelace-style/shoelace@2.18.0/cdn/'
);

//window.api = api

const initiateAppContextData = () => {
  return {
    ...localStorage,
    userData: isJSON(localStorage['userData'])
      ? JSON.parse(localStorage['userData'])
      : null,
    ibl_user_public_metadata: isJSON(
      localStorage.getItem('ibl_user_public_metadata')
    )
      ? JSON.parse(localStorage.getItem('ibl_user_public_metadata'))
      : null,
    userMetaData: {},
  };
};

function App() {
  const [appData, setAppData] = useState(() => {
    return {
      ...initiateAppContextData(),
    };
  });
  const { translate } = useIblTranslation();
  const { notification, setNotification, notificationRef } = useNotifications();
  const [tenantMetaData, setTenantMetaData] = React.useState({});
  const [userAdminCapabilities, setUserAdminCapabilities] = useState([]);

  const checkUserAdminCapabilities = async () => {
    const adminCheckInLocalstorage = localStorage.getItem('userAdminCheck');
    let userAdminCheck = isJSON(adminCheckInLocalstorage)
      ? JSON.parse(adminCheckInLocalstorage)
      : false;
    if (!userAdminCheck) {
      userAdminCheck = await getUserAdminCapabilities();
    }
    //REDUCING REDUNDANT ENDPOINT CALL
    localStorage.setItem('userAdminCheck', JSON.stringify(userAdminCheck));
    setUserAdminCapabilities(userAdminCheck);
  };

  useEffect(() => {
    api.initiateAxdClientsWithToken(appData.axd_token);
    const internalDomains = [
      '@acilearning.com',
      '@happiestminds.com',
      '@yopmail.com',
      '@itpro.tv',
      '@practice-labs.com',
    ];
    // internal staff that need access to devless
    const whitelistedUsers = [
      'jake.price@acilearning.com',
      'julianna.varga@acilearning.com',
      'tara.culliney@acilearning.com',
    ];
    const userEmail = appData?.userData?.user_email || '';
    const isInternal = internalDomains.some((domain) =>
      userEmail.endsWith(domain)
    );
    const isWhiteListed = whitelistedUsers.includes(userEmail);
    if (
      process.env.REACT_APP_MODE === 'production' &&
      window.ChurnZero &&
      typeof window.ChurnZero.push === 'function' &&
      userEmail &&
      (!isInternal || isWhiteListed) // skip over internal accounts except for whitelisted users
    ) {
      global.ChurnZero.push([
        'setContact',
        appData?.tenant, // tenant id (Salesforce Account ID)
        userEmail, // username (email address)
      ]);
    }
    checkUserAdminCapabilities();
  }, []);

  useEffect(() => {
    if (appData.userMetaData?.language) {
      translate(appData.userMetaData.language);
    }
  }, [appData.userMetaData]);

  useEffect(() => {
    const fetchTokenAndLoginZendesk = async () => {
      try {
        if (
          !appData?.userData?.user_email ||
          !appData?.userData?.user_id ||
          !appData?.userMetaData?.name
        ) {
          console.warn('Required user data not available in appData.');
          return;
        }

        const response = await fetch(process.env.REACT_APP_ZENDESK_TOKEN_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify({
            name: appData.userMetaData?.name,
            email: appData.userData.user_email,
            external_id: appData.userData.user_id.toString(),
          }),
        });

        const data = await response.json();
        const accessToken = data.access_token;

        // Login to Zendesk using the token
        if (window.zE && typeof window.zE === 'function') {
          window.zE('messenger', 'loginUser', (callback) => {
            callback(accessToken);
          });
          window.zE('messenger:set', 'conversationFields', [
            {
              currentPage: window.location.pathname,
              product: 'MyACI',
              role: 'B2BAdmin',
            },
          ]);
        } else {
          console.warn('Zendesk widget not available yet.');
        }
      } catch (error) {
        console.error('Error fetching token or logging into Zendesk:', error);
      }
    };

    fetchTokenAndLoginZendesk();
  }, [appData]);

  return (
    <>
      <GlobalStyles />
      <AppDataContext.Provider
        value={{
          appData,
          setAppData,
          notification,
          notificationRef,
          setNotification,
          tenantMetaData,
          setTenantMetaData,
          userAdminCapabilities,
        }}
      >
        <TourProvider steps={[]}>
          <RouterProvider
            fallbackElement={
              <IBLSpinLoader
                size={35}
                color={'var(--accent)'}
                containerHeight={'100%'}
              />
            }
            router={route(appData, setAppData, userAdminCapabilities)}
          />
        </TourProvider>
      </AppDataContext.Provider>
    </>
  );
}

const GlobalStyles = createGlobalStyle`
  html {
    --grey: ${GLOBAL_COLORS.grey};
    --font: ${GLOBAL_COLORS.font};
    --accent: ${GLOBAL_COLORS.primary};
    --accenthover: ${GLOBAL_COLORS.secondary};
    --accent-2: ${GLOBAL_COLORS.tertiary};
    --gainsboro: ${GLOBAL_COLORS.grey_secondary};
    --white-smoke: ${GLOBAL_COLORS.white_smoke};
    --white: white;
    --black: black;
    --3: white;
  }
`;

export default App;
